import React from 'react';

/**
 * Renders an email's HTML body in a sandboxed iframe (Freshdesk #656).
 *
 * Why not `dangerouslySetInnerHTML`: lender emails ship their own
 * stylesheets. Ford Credit's "We've reviewed your complaint" emails carry an
 * `@media (prefers-color-scheme: dark)` block with `!important` rules on bare
 * `p`, `h1-h4`, `a`, `ul`, `li` and `html, body` selectors. Injected inline,
 * those rules apply to the WHOLE PCP page for any staff member whose Windows
 * is in dark mode: the email's text goes white on PCP's light grey pane and
 * the surrounding PCP text turns grey too (Jiji's screenshots on #656).
 *
 * In its own frame the email's CSS cannot reach PCP, PCP's CSS cannot reach
 * the email, and no script in the email can run (`sandbox` without
 * `allow-scripts`). The lender's dark-mode block is neutralised so the email
 * always renders in the lender's light design, whatever the viewer's OS
 * setting -- the pane itself is light-only, so a dark email inside it would
 * look wrong anyway.
 *
 * The frame fills the box it is given and the email scrolls inside it. It
 * deliberately does NOT size itself to its content: a first version did, and
 * the height writes fought the lane page's own ResizeObserver ("ResizeObserver
 * loop completed with undelivered notifications" on Dec's localhost test).
 * The email's own CSS cannot switch that scrolling off (Freshdesk #705, see
 * SCROLL_STYLE). Links open in a new tab, as they did from the pane before.
 */

/** Any `prefers-color-scheme: dark` condition is rewritten to a value that
 * never matches, so the block is parsed but never applied. */
const DARK_MODE_CONDITION_RE = /prefers-color-scheme\s*:\s*dark/gi;
const NEVER_MATCHES = 'prefers-color-scheme: pcp-never';

const HEAD_OPEN_RE = /<head\b[^>]*>/i;
const DOCTYPE_RE = /^\s*<!doctype\b/i;

/** No scripts, no plugins, no form posts; images and inline styles allowed
 * (lender emails are picture-heavy and the pane showed them before). */
const CSP = "default-src 'none'; img-src * data: blob:; style-src 'unsafe-inline' https:; font-src https: data:; form-action 'none'";

const OWNED_STYLE =
  'html{color-scheme:light only}' +
  'body{margin:0;padding:8px;font:14px/1.6 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#1f2937;background:#fff;overflow-wrap:break-word;word-wrap:break-word}' +
  'img{max-width:100%;height:auto}';

const INJECTED_HEAD =
  '<meta charset="utf-8">' +
  `<meta http-equiv="Content-Security-Policy" content="${CSP}">` +
  '<meta name="color-scheme" content="light only">' +
  '<base target="_blank">' +
  `<style>${OWNED_STYLE}</style>`;

/**
 * Freshdesk #705 item 6: the frame always scrolls. Some lender emails switch
 * scrolling off on their own <html> and <body> (VWFS "Multi Representation
 * Complaint": `<html style="overflow-y:hidden">`, 245 of 1,870 at 28 Oct 2026), which leaves
 * staff looking at the top of the email in our fixed-height frame with no way
 * down. So the root always scrolls, both ways (a wide table still reaches its
 * right edge), and body never clips and grows with the email (an email's own
 * `height:100%` would otherwise leave its last line flush against the frame's
 * bottom edge).
 *
 * These rules go at the very END of the document, after everything the email
 * brings, and are `!important` on `:root`: that outranks the email's inline
 * style="" attributes and any html/body rule in its own <style> blocks, even
 * `!important` ones. (A stylesheet cannot outrank an inline style="...
 * !important" on html/body.) Appended after `</html>`, the parser makes it the
 * last element of body, where it still applies.
 */
const SCROLL_STYLE = '<style data-pcp-scroll>:root{overflow:auto!important}:root>body{overflow:visible!important;height:auto!important}</style>';

/**
 * Builds the complete document the frame shows. A lender email that is
 * already a full document (Ford's is: `<html><head>...`) keeps its own head
 * with our tags inserted at the top of it; a fragment is wrapped. Either way
 * the scroll rules come last.
 */
export function buildEmailFrameDocument(html: string): string {
  const neutralised = (html || '').replace(DARK_MODE_CONDITION_RE, NEVER_MATCHES);
  if (HEAD_OPEN_RE.test(neutralised)) {
    const withHead = neutralised.replace(HEAD_OPEN_RE, (open) => `${open}${INJECTED_HEAD}`);
    const doc = DOCTYPE_RE.test(withHead) ? withHead : `<!doctype html>${withHead}`;
    return `${doc}${SCROLL_STYLE}`;
  }
  return `<!doctype html><html><head>${INJECTED_HEAD}</head><body>${neutralised}</body></html>${SCROLL_STYLE}`;
}

export const EMAIL_FRAME_SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox';

interface EmailBodyFrameProps {
  /** The email HTML (or plain text) exactly as stored; cid: images should
   * already be resolved by the caller. */
  html: string;
  title?: string;
  className?: string;
}

export default function EmailBodyFrame({ html, title = 'Email body', className }: EmailBodyFrameProps) {
  return (
    <iframe
      className={className}
      title={title}
      sandbox={EMAIL_FRAME_SANDBOX}
      srcDoc={buildEmailFrameDocument(html)}
      style={{ width: '100%', height: '100%', border: 0, display: 'block', background: '#fff' }}
    />
  );
}
